import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, CalendarDays, Clock3, Mail, MapPin, MessageCircle, Phone, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BOOKING_URL,
  CLINIC_ADDRESS_LINE_1,
  CLINIC_ADDRESS_LINE_2,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  LIVE_CHAT_URL,
  PHONE_NUMBER,
  PHONE_TEL,
  WHATSAPP_URL,
} from '@/config/site';

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCallbackRequest = searchParams.get("callback") === "1";
  const callbackTopic = searchParams.get("topic") || "";

  useEffect(() => {
    if (!isCallbackRequest) return;
    setFormData((prev) => {
      if (prev.message.trim()) return prev;
      const topicLabel = callbackTopic.trim() || "General Enquiry";
      return {
        ...prev,
        message: `Callback request: ${topicLabel}\nBest number to call: \nPreferred time: `,
      };
    });
  }, [callbackTopic, isCallbackRequest]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const endpoint = `${apiBase}/api/contact-requests`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
          source: isCallbackRequest ? "contact-form-callback-followup" : "contact-form",
          page: `${window.location.pathname}${window.location.search}`,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.detail || "Unable to send your message right now.");
      }

      toast.success("Message sent", {
        description: result?.message || "Our team will respond shortly.",
      });

      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error("Could not send message", {
        description: error.message || "Please try again or call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  return (
    <div className="pb-mobile-bar" data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-100 via-white to-white pt-10 pb-8 md:pt-14 md:pb-10 lg:pt-16 lg:pb-12" data-testid="contact-hero">
        <div className="pointer-events-none absolute -left-20 top-0 h-64 w-64 rounded-full bg-accent-gold/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-stone-200/40 blur-3xl" />
        <div className="container-luxury relative z-10">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div className="max-w-3xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-caption mb-3 text-accent-gold"
              >
                Contact Specialist Dental London
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-display mb-4 text-accent-gold"
              >
                Let&apos;s Plan Your Visit
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-body-large"
              >
                Questions, booking requests, or specialist enquiries. Reach us directly and
                we&apos;ll guide you to the right next step.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-6 flex flex-wrap gap-3"
              >
                <a href={PHONE_TEL} className="inline-flex items-center gap-2 rounded-full border border-accent-gold/45 bg-accent-gold/10 px-4 py-2 font-body text-sm text-accent-gold transition-colors hover:bg-accent-gold/15">
                  <Phone size={15} strokeWidth={1.7} className="text-accent-gold" />
                  {PHONE_NUMBER}
                </a>
                <a href={CONTACT_MAILTO} className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 font-body text-sm text-charcoal transition-colors hover:border-stone-400">
                  <Mail size={15} strokeWidth={1.7} className="text-accent-gold" />
                  {CONTACT_EMAIL}
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-3xl border border-stone-200 bg-white/90 p-6 shadow-[0_14px_30px_rgba(41,37,36,0.08)] backdrop-blur-sm"
            >
              <p className="font-accent text-xs uppercase tracking-widest text-accent-gold">Clinic Location</p>
              <h2 className="mt-2 font-heading text-2xl text-charcoal">New Malden, London</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-stone-600">
                {CLINIC_ADDRESS_LINE_1}<br />
                {CLINIC_ADDRESS_LINE_2}
              </p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-stone-100 px-3 py-1.5 font-body text-xs text-stone-600">
                <Building2 size={14} strokeWidth={1.6} className="text-accent-gold" />
                Inside The Health Hub
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="section-spacing pt-8 md:pt-12 lg:pt-14" data-testid="contact-grid">
        <div className="container-luxury">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8">
                <h2 className="text-subheadline mb-6">Visit or Contact Us</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-stone-100">
                      <MapPin size={18} className="text-accent-gold" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-charcoal">Address</p>
                      <p className="font-body text-sm text-stone-600">
                        {CLINIC_ADDRESS_LINE_1}<br />
                        {CLINIC_ADDRESS_LINE_2}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-stone-100">
                      <Phone size={18} className="text-accent-gold" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-charcoal">Phone</p>
                      <a href={PHONE_TEL} className="font-body text-sm text-stone-600 transition-colors hover:text-charcoal">
                        {PHONE_NUMBER}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-stone-100">
                      <Mail size={18} className="text-accent-gold" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-charcoal">Email</p>
                      <a href={CONTACT_MAILTO} className="font-body text-sm text-stone-600 transition-colors hover:text-charcoal">
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-stone-200 bg-stone-50 p-6 md:p-8">
                <h3 className="font-heading text-xl text-charcoal">Opening Hours</h3>
                <div className="mt-4 space-y-2 font-body text-sm text-stone-600">
                  <p className="flex items-center gap-2">
                    <Clock3 size={15} strokeWidth={1.6} className="text-accent-gold" />
                    Monday - Friday: 9:00 AM - 6:00 PM
                  </p>
                  <p>Saturday: By appointment</p>
                  <p>Sunday: Closed</p>
                </div>
                <p className="mt-4 rounded-xl border border-stone-200 bg-white px-4 py-3 font-body text-xs text-stone-600">
                  Same-day responses available for urgent enquiries.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <a
                  href={BOOKING_URL}
                  data-testid="contact-book-btn"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <CalendarDays size={16} strokeWidth={1.7} />
                  Book Appointment
                  <ArrowRight size={16} strokeWidth={1.5} />
                </a>
                <a
                  href={LIVE_CHAT_URL}
                  data-testid="contact-chat-btn"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Let&apos;s Talk
                  <MessageCircle size={16} strokeWidth={1.5} />
                </a>
                <a
                  href={WHATSAPP_URL}
                  data-testid="contact-whatsapp-btn"
                  className="sm:col-span-2 inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[#25D366]/30 bg-[#25D366]/10 px-6 font-body text-sm font-medium text-[#1b8f47] transition-colors hover:bg-[#25D366]/20"
                >
                  WhatsApp Us
                  <MessageCircle size={16} strokeWidth={1.5} />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8 lg:p-10">
              <h2 className="text-subheadline mb-2">Send a Message</h2>
              <p className="font-body text-stone-600 mb-8">
                Share your question and we&apos;ll reply promptly.
              </p>
              {isCallbackRequest && (
                <div className="mb-6 rounded-2xl border border-accent-gold/50 bg-accent-gold/10 px-4 py-3">
                  <p className="font-body text-sm text-charcoal">
                    Callback request topic: <span className="font-semibold">{callbackTopic || "General Enquiry"}</span>
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="font-body font-medium text-charcoal">
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    className="mt-2 h-12 bg-stone-50 border-stone-200 focus:border-accent-gold focus:ring-accent-gold/20"
                    placeholder="Your name"
                  />
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email" className="font-body font-medium text-charcoal">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                      className="mt-2 h-12 bg-stone-50 border-stone-200 focus:border-accent-gold focus:ring-accent-gold/20"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-body font-medium text-charcoal">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className="mt-2 h-12 bg-stone-50 border-stone-200 focus:border-accent-gold focus:ring-accent-gold/20"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="font-body font-medium text-charcoal">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    className="mt-2 min-h-[140px] bg-stone-50 border-stone-200 focus:border-accent-gold focus:ring-accent-gold/20 resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full h-12 bg-charcoal hover:bg-charcoal-light text-warm-white rounded-full font-body font-medium"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={18} className="ml-2" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-spacing bg-stone-50" data-testid="map-section" id="map-anchor">
        <div className="container-luxury">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-caption mb-4 text-accent-gold">Location</p>
            <h2 className="text-headline mb-4">Find Us in New Malden</h2>
            <p className="text-body">
              {CLINIC_ADDRESS_LINE_1}. {CLINIC_ADDRESS_LINE_2}. Easy to reach by train, car, or bus.
            </p>
          </div>

          <div className="aspect-[16/9] lg:aspect-[21/9] rounded-2xl overflow-hidden border border-stone-200 shadow-[0_14px_30px_rgba(41,37,36,0.08)]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2489.7012!2d-0.2554!3d51.3839!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDIzJzAyLjAiTiAwwrAxNScxOS40Ilc!5e0!3m2!1sen!2suk!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Specialist Dental London - New Malden Location"
            />
          </div>

          <div className="grid gap-4 mt-8 md:grid-cols-3">
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <h3 className="font-heading text-lg text-charcoal mb-2">By Train</h3>
              <p className="font-body text-sm text-stone-600">
                New Malden Station is a short walk from the clinic.
              </p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <h3 className="font-heading text-lg text-charcoal mb-2">By Car</h3>
              <p className="font-body text-sm text-stone-600">
                Local parking options are available around the High Street.
              </p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <h3 className="font-heading text-lg text-charcoal mb-2">By Bus</h3>
              <p className="font-body text-sm text-stone-600">
                Multiple bus routes stop close to The Health Hub.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing section-hero-echo-dark-alt" data-testid="contact-cta">
        <div className="container-luxury text-center">
          <h2 className="mb-6 font-heading text-3xl font-medium tracking-tight text-warm-white md:text-4xl lg:text-5xl">
            Ready to Book?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-relaxed text-stone-200 md:text-xl">
            Book directly online, or contact us for help planning the right appointment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              data-testid="contact-final-book"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-warm-white px-8 font-body font-medium text-charcoal transition-all duration-300 hover:bg-accent-gold-light"
            >
              Book Appointment
              <ArrowRight size={18} strokeWidth={1.5} />
            </a>
            <Link
              to="/referrals"
              data-testid="contact-referral-btn"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/70 bg-white/10 px-8 font-body font-medium text-warm-white transition-all duration-300 hover:bg-white hover:text-charcoal"
            >
              Specialist Referrals
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
